import type {
	FormErrorCode,
	FormFieldDefinitionModel,
	FormFieldErrorCode,
} from './form-types';

/**
 * Plain default wording for the form-level codes the public endpoint returns, so a site
 * without its own messages still shows something readable (01-design.md §3). The server
 * sends them as `message`; the storefront renderer shows them in its error summary.
 */
export const FORM_ERROR_MESSAGES: Readonly<
	Record<FormErrorCode | 'validation_failed', string>
> = {
	captcha_failed: 'We could not verify this submission. Please try again.',
	captcha_expired: 'The verification check expired. Please try again.',
	origin_not_allowed: 'This site cannot submit this form.',
	form_not_found: 'This form is not available.',
	rate_limited: 'Too many submissions. Please try again later.',
	validation_failed: 'Please correct the highlighted fields.',
};

/**
 * The plain wording for a field error code (docs/claude/forms/01-design.md §3), shared by
 * the form renderer, the CRM's custom field editor and the server's refusals, so a person
 * reads the same message wherever the same value is refused. Pages wrap it in t().
 */
export function describeFormFieldError(
	field: Pick<FormFieldDefinitionModel, 'label' | 'validationRules'> &
		Partial<Pick<FormFieldDefinitionModel, 'fieldType'>>,
	code: FormFieldErrorCode,
): string {
	const rules = field.validationRules ?? {};
	const label = field.label;

	// PD-16691 — a signature is drawn, not typed, so the usual wording ("too long", "not
	// in the expected format") would not tell the visitor what to do.
	if (field.fieldType === 'signature') {
		switch (code) {
			case 'required':
				return 'Sign in the box to continue';
			case 'too_long':
				return 'The signature is too large. Clear it and sign again.';
			case 'invalid_format':
			case 'invalid_type':
				return 'The signature could not be read. Clear it and sign again.';
			default:
				break;
		}
	}

	switch (code) {
		case 'required':
			// A consent label is the whole agreement text, too long to repeat in a message.
			return field.fieldType === 'consent'
				? 'Check this box to continue'
				: `${label} is required`;
		case 'too_long':
			return rules.maxLength != null
				? `${label} must be at most ${rules.maxLength} characters`
				: `${label} is too long`;
		case 'too_short':
			return `${label} must be at least ${rules.minLength ?? 1} characters`;
		case 'below_min':
			return `${label} must be at least ${String(rules.min)}`;
		case 'above_max':
			return `${label} must be at most ${String(rules.max)}`;
		case 'invalid_number':
			return `${label} must be a number`;
		case 'invalid_email':
			return `${label} must be an email address`;
		case 'invalid_phone':
			return `${label} must be a phone number`;
		case 'invalid_url':
			return `${label} must be a web address starting with http:// or https://`;
		case 'invalid_date':
			return `${label} must be a date`;
		case 'invalid_datetime':
			return `${label} must be a date and time`;
		case 'invalid_option':
			return `${label} must be one of its listed choices`;
		case 'invalid_format':
			return `${label} is not in the expected format`;
		case 'invalid_type':
			return `${label} has a value of the wrong type`;
		default:
			return `${label} is not valid`;
	}
}
